import { Button } from '@components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@components/ui/card'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@components/ui/input-otp'
import Loader from '@components/ui/loader'
import { useMutation, useQuery } from '@connectrpc/connect-query'
import { parseErrorMessage } from '@lib/errors'
import { LoginView } from '@lib/views'
import React, { Dispatch, FC, FormEvent, SetStateAction, useState } from 'react'
import { Helmet } from 'react-helmet'
import { toast } from 'sonner'
import {
  exchange,
  verifyEmailVerificationChallenge,
  whoami,
} from '@gen/api/v1/api-ApiService_connectquery'

interface VerifyEmailAddressViewProps {
  setView: Dispatch<SetStateAction<LoginView>>
}

const VerifyEmailAddressView: FC<VerifyEmailAddressViewProps> = ({
  setView,
}) => {
  const exchangeMutation = useMutation(exchange)
  const verifyEmailAddressMutation = useMutation(
    verifyEmailVerificationChallenge,
  )

  const { data: whoamiRes, refetch: refetchWhoami } = useQuery(whoami)

  const [code, setCode] = useState<string>('')
  const [submitting, setSubmitting] = useState<boolean>(false)

  const deriveNextView = (): LoginView | undefined => {
    if (!whoamiRes?.intermediateSession?.googleUserId) {
      return LoginView.RegisterPassword
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    try {
      await verifyEmailAddressMutation.mutateAsync({
        code,
      })

      await refetchWhoami()

      const nextView = deriveNextView()
      if (nextView) {
        setView(nextView)
      }

      await exchangeMutation.mutateAsync({})

      setSubmitting(false)
    } catch (error) {
      setSubmitting(false)
      const message = parseErrorMessage(error)
      toast.error('Could not verify email address', {
        description: message,
      })
    }
  }

  return (
    <>
      <Helmet>
        <title>Verify email address | Ancho</title>
      </Helmet>

      <Card className="max-w-sm">
        <CardHeader>
          <CardTitle>Verify email address</CardTitle>
          <CardDescription>
            An verification code has been sent to [email placeholder]. Please
            enter the code below.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="w-full flex flex-col items-center [&>button]:mt-2"
            onSubmit={handleSubmit}
          >
            <InputOTP maxLength={6} onChange={setCode}>
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>

            <Button disabled={code.length < 6 || submitting} type="submit">
              {submitting && <Loader />}
              Continue
            </Button>
          </form>
        </CardContent>
      </Card>
    </>
  )
}

export default VerifyEmailAddressView
